/**
 * State Manager
 * Handles state transitions and state machine logic for triangle navigation
 */

import { STATES } from './constants.js';

export default class StateManager {
  constructor() {
    this.currentState = {
      type: STATES.START,
      dimension: null,
      area: null,
    };
    this.previousState = null;
    this.data = null;
    this.listeners = [];
  }

  /**
   * Initialize with data from CMS
   * @param {Object} data - Navigation data
   */
  init(data) {
    this.data = data;

    // Determine initial state based on current page
    if (data.currentState) {
      this.setStateFromString(data.currentState);
    }

    return this;
  }

  /**
   * Set state from string (e.g., "start", "dimension:barnets-utvikling", "area:barnets-utvikling:helse")
   */
  setStateFromString(stateString) {
    const parts = stateString.split(':');

    switch (parts[0]) {
      case 'start':
        this.setState({ type: STATES.START, dimension: null, area: null });
        break;
      case 'dimension':
        this.setState({
          type: STATES.DIMENSION,
          dimension: parts[1] || null,
          area: null,
        });
        break;
      case 'area':
        this.setState({
          type: STATES.AREA,
          dimension: parts[1] || null,
          area: parts[2] || null,
        });
        break;
    }
  }

  /**
   * Update data (called when navigating to new page)
   */
  updateData(newData) {
    this.data = newData;
    if (newData.currentState) {
      this.setStateFromString(newData.currentState);
    }
  }

  /**
   * Set current state
   */
  setState(newState, options = { animate: true }) {
    this.previousState = { ...this.currentState };
    this.currentState = newState;

    // Notify listeners
    this.notifyListeners(options);
  }

  /**
   * Navigate to dimension (from start state only)
   * For other transitions, navigate via URL and let the new page set state
   */
  goToDimension(dimensionHandle, options = { animate: true }) {
    if (this.currentState.type !== STATES.START) {
      // This is expected - cross-state navigation happens via URL
      return;
    }

    this.setState(
      {
        type: STATES.DIMENSION,
        dimension: dimensionHandle,
        area: null,
      },
      options
    );
  }

  /**
   * Navigate to area (from dimension state only)
   * For other transitions, navigate via URL and let the new page set state
   */
  goToArea(areaHandle, options = { animate: true }) {
    if (this.currentState.type !== STATES.DIMENSION) {
      // This is expected - cross-state navigation happens via URL
      return;
    }

    this.setState(
      {
        type: STATES.AREA,
        dimension: this.currentState.dimension,
        area: areaHandle,
      },
      options
    );
  }

  /**
   * Go back to previous state
   */
  goBack(options = { animate: true }) {
    switch (this.currentState.type) {
      case STATES.AREA:
        // Area -> Dimension
        this.setState(
          {
            type: STATES.DIMENSION,
            dimension: this.currentState.dimension,
            area: null,
          },
          options
        );
        break;

      case STATES.DIMENSION:
        // Dimension -> Start
        this.setState(
          {
            type: STATES.START,
            dimension: null,
            area: null,
          },
          options
        );
        break;

      case STATES.START:
        // Already at start, can't go back
        console.warn('Already at start state');
        break;
    }
  }

  /**
   * Get current state
   */
  getState() {
    return this.currentState;
  }

  /**
   * Get previous state
   */
  getPreviousState() {
    return this.previousState;
  }

  /**
   * Check if we can go back
   */
  canGoBack() {
    return this.currentState.type !== STATES.START;
  }

  /**
   * Get direction of state transition
   * @returns {'forward'|'backward'|'same'}
   */
  getTransitionDirection() {
    if (!this.previousState) return 'same';

    const stateOrder = {
      [STATES.START]: 0,
      [STATES.DIMENSION]: 1,
      [STATES.AREA]: 2,
    };

    const currentLevel = stateOrder[this.currentState.type];
    const previousLevel = stateOrder[this.previousState.type];

    if (currentLevel > previousLevel) return 'forward';
    if (currentLevel < previousLevel) return 'backward';
    return 'same';
  }

  /**
   * Add state change listener
   */
  onChange(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  /**
   * Notify all listeners of state change
   */
  notifyListeners(options) {
    this.listeners.forEach((callback) => {
      callback(this.currentState, this.previousState, options);
    });
  }

  /**
   * Get data for current state
   */
  getStateData() {
    if (!this.data) return null;

    switch (this.currentState.type) {
      case STATES.START:
        return {
          dimensions: this.data.dimensions,
        };

      case STATES.DIMENSION:
        return {
          dimension: this.data.dimensions?.find(
            (d) => d.handle === this.currentState.dimension
          ),
          areas: this.data.areas?.[this.currentState.dimension],
        };

      case STATES.AREA:
        const areas = this.data.areas?.[this.currentState.dimension] || [];
        return {
          dimension: this.data.dimensions?.find(
            (d) => d.handle === this.currentState.dimension
          ),
          area: areas.find((a) => a.handle === this.currentState.area),
          allAreas: areas,
        };

      default:
        return null;
    }
  }
}
